<template>
    <!-- a news card: the photo (whole, on its own blurred copy), the date, the title and a few lines of the text -->
    <router-link v-if="article" :to="{ name: 'Article', params: { id: article.id } }" class="article-card">
        <span class="article-card__media">
            <span class="article-card__backdrop" :style="{ backgroundImage: `url('${image}')` }" />
            <img :src="image" :alt="article.title" class="article-card__image" loading="lazy">
            <span v-if="article.gallery && article.gallery.length" class="article-card__gallery">
                <v-icon x-small color="white">mdi-image-multiple-outline</v-icon>{{ article.gallery.length }}
            </span>
        </span>
        <span class="article-card__body">
            <span class="article-card__date">
                <v-icon x-small>mdi-calendar-outline</v-icon>{{ date }}
            </span>
            <span class="article-card__title">{{ article.title }}</span>
            <span class="article-card__excerpt">{{ excerpt }}</span>
            <span class="article-card__more">Διάβασε <v-icon x-small>mdi-arrow-right</v-icon></span>
        </span>
    </router-link>
</template>

<script>
    import momentTimezone from 'moment-timezone'

    // the text of an article without its markup, for the cards and the carousel
    export function articleExcerpt(article, length = 160) {
        if (!article || !article.content) return ''
        try {
            const doc = new DOMParser().parseFromString(article.content, 'text/html')
            const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
            return text.length > length ? text.slice(0, length).replace(/\s+\S*$/, '') + '…' : text
        } catch (e) {
            return ''
        }
    }

    export default {
        props: {
            article: {
                type: Object,
                default: null
            }
        },
        computed: {
            image() {
                return `https://storage.googleapis.com/${this.article.image}`
            },
            date() {
                return this.article.date ? momentTimezone(this.article.date).tz('Europe/Athens').locale('el').format('D MMMM YYYY') : ''
            },
            excerpt() {
                return articleExcerpt(this.article)
            }
        }
    }
</script>

<style scoped>
.article-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: 12px;
    background: #232323;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.14);
    color: #fff !important;
    text-align: left;
    text-decoration: none;
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.15s;
}

.article-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 30px rgba(3, 122, 222, 0.16);
}

.article-card__media {
    position: relative;
    display: block;
    aspect-ratio: 16 / 9;
    background: #232323;
    overflow: hidden;
}

.article-card__media::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.article-card__backdrop {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(18px) brightness(0.7);
    transform: scale(1.15);
}

.article-card__image {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s;
}

.article-card:hover .article-card__image {
    transform: scale(1.03);
}

.article-card__gallery {
    position: absolute;
    right: 10px;
    top: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
}

.article-card__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 auto;
    padding: 16px 18px 18px;
}

.article-card__date {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
}

.article-card__date .v-icon {
    color: #03e5b7 !important;
}

.article-card__title {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 3;
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.5rem;
    line-height: 1.1;
    letter-spacing: 0.01em;
}

.article-card:hover .article-card__title {
    color: #03e5b7;
}

.article-card__excerpt {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 3;
    overflow: hidden;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.9rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.65);
}

.article-card__more {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: auto;
    padding-top: 4px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.82rem;
    font-weight: 700;
    color: #03e5b7;
}

.article-card__more .v-icon {
    color: inherit !important;
    transition: transform 0.15s;
}

.article-card:hover .article-card__more .v-icon {
    transform: translateX(3px);
}
</style>
